import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { Cancel as CancelIcon, AddCircle as AddCircleIcon, Add as AddIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { scheduleService, CreateMatchRequest } from '@/services/scheduleService';
import { playerService } from '@/services/playerService';
import { invalidateMatchQueries } from '@/utils/calendarInvalidation';
import { emitMatchChange } from '@/utils/calendarEvents';

interface AddMatchDialogProps {
  open: boolean;
  onClose: () => void;
}

const AddMatchDialog: React.FC<AddMatchDialogProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState<Partial<CreateMatchRequest>>({
    matchType: 'REGULAR_SEASON',
  });
  const [dateTime, setDateTime] = useState<Dayjs | null>(dayjs().add(1, 'day'));
  const [error, setError] = useState<string>('');

  const queryClient = useQueryClient();

  // Fetch players to get available leagues
  const { data: players = [] } = useQuery('players', playerService.getPlayers);

  // Get unique leagues from player assignments
  const availableLeagues = players.reduce((leagues: any[], player) => {
    player.leagueAssignments?.forEach((assignment: any) => {
      if (!leagues.find(l => l.id === assignment.league.id)) {
        leagues.push(assignment.league);
      }
    });
    return leagues;
  }, []);

  // Get teams for selected league
  const availableTeams = formData.leagueId 
    ? players.reduce((teams: string[], player) => {
        player.leagueAssignments?.forEach((assignment: any) => {
          if (assignment.league.id === formData.leagueId && 
              !teams.includes(assignment.teamName) && 
              assignment.teamName !== 'TBD') { // Filter out TBD placeholder teams
            teams.push(assignment.teamName);
          }
        });
        return teams;
      }, [])
    : [];

  const createMatchMutation = useMutation(scheduleService.createMatch, {
    onSuccess: (newMatch) => {
      // Use centralized invalidation utility
      invalidateMatchQueries(queryClient);
      
      // Emit event for global listeners
      emitMatchChange('created', newMatch);
      
      handleClose();
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Failed to create match');
    },
  });

  const handleClose = () => {
    setFormData({ matchType: 'REGULAR_SEASON' });
    setDateTime(dayjs().add(1, 'day'));
    setError('');
    onClose();
  };

  const handleSubmit = () => {
    if (!formData.leagueId || !formData.homeTeam || !formData.awayTeam || !dateTime) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.homeTeam === formData.awayTeam) {
      setError('Home and away teams must be different');
      return;
    }

    const matchData: CreateMatchRequest = {
      leagueId: formData.leagueId,
      homeTeam: formData.homeTeam,
      awayTeam: formData.awayTeam,
      dateTime: dateTime.toISOString(),
      location: formData.location,
      matchType: formData.matchType || 'REGULAR_SEASON',
    };

    createMatchMutation.mutate(matchData);
  };

  const handleInputChange = (field: keyof CreateMatchRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setError('');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableAutoFocus>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AddCircleIcon />Add New Match</Box></DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>League</InputLabel>
                <Select
                  value={formData.leagueId || ''}
                  onChange={(e) => handleInputChange('leagueId', e.target.value)}
                  label="League"
                >
                  {availableLeagues.map((league) => (
                    <MenuItem key={league.id} value={league.id}>
                      {league.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Home Team</InputLabel>
                <Select
                  value={formData.homeTeam || ''}
                  onChange={(e) => handleInputChange('homeTeam', e.target.value)}
                  label="Home Team"
                  disabled={!formData.leagueId}
                >
                  {availableTeams.map((team) => (
                    <MenuItem key={team} value={team}>
                      {team}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Away Team</InputLabel>
                <Select
                  value={formData.awayTeam || ''}
                  onChange={(e) => handleInputChange('awayTeam', e.target.value)}
                  label="Away Team"
                  disabled={!formData.leagueId}
                >
                  {availableTeams.map((team) => (
                    <MenuItem key={team} value={team}>
                      {team}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <DateTimePicker
                label="Date & Time *"
                value={dateTime}
                onChange={setDateTime}
                slotProps={{ textField: { fullWidth: true } }}
                minDateTime={dayjs()}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location || ''}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="Enter match location"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Match Type</InputLabel>
                <Select
                  value={formData.matchType || 'REGULAR_SEASON'}
                  onChange={(e) => handleInputChange('matchType', e.target.value)}
                  label="Match Type"
                >
                  <MenuItem value="REGULAR_SEASON">Regular Season</MenuItem>
                  <MenuItem value="TOURNAMENT">Tournament</MenuItem>
                  <MenuItem value="PLAYOFF">Playoff</MenuItem>
                  <MenuItem value="FRIENDLY">Friendly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose} startIcon={<CancelIcon />}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMatchMutation.isLoading}
            startIcon={createMatchMutation.isLoading ? undefined : <AddIcon />}
          >
            {createMatchMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              'Create Match'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddMatchDialog;
